import { Button, Form, Grid, Modal } from '@mairistem/composants';
import _ from 'lodash';
import React from 'react';

import TEXTE_RELANCE_DEFAUT from '@/assets/text/texteRelanceDefaut';
import { ElementsDisponibles } from '@/components/EditeurMail';
import { RELANCE_ELEMENTS_DISPONIBLES } from '@/constants';
import type { ElementDisponibleType } from '@/types';

import EditeurContenu from './EditeurContenu';

type ModalTexteRelanceProps = {
  texteRelance?: string;
  loading?: boolean;
  /** Éléments insérables affichés dans la barre latérale. Défaut : relance après-échéance (impayés). */
  elements?: ElementDisponibleType[];
  /** Texte par défaut (bouton « rétablir » + valeur initiale si texteRelance vide). */
  texteDefaut?: string;
  onClose: () => void;
  onSave?: (texte: string) => void;
};

const ModalTexteRelance = (props: ModalTexteRelanceProps) => {
  const {
    texteRelance,
    loading = false,
    elements = RELANCE_ELEMENTS_DISPONIBLES,
    texteDefaut = TEXTE_RELANCE_DEFAUT,
    onClose,
    onSave,
  } = props;

  const [texte, setTexte] = React.useState<string>(
    _.isEmpty(texteRelance) ? texteDefaut : texteRelance,
  );
  const [modifie, setModifie] = React.useState<boolean>(false);

  const texteInitial = React.useRef(texteRelance || texteDefaut);
  const textareaRef = React.useRef<any>(null);
  const selectionStartRef = React.useRef<number>(0);
  const selectionEndRef = React.useRef<number>(0);

  const handleChange = React.useCallback((e, { value }) => {
    setTexte(value);
    setModifie(value !== texteInitial.current);
  }, []);

  const handleBlur = React.useCallback((e: React.FocusEvent<HTMLTextAreaElement>) => {
    // Sauvegarder la sélection (début et fin) lors de la perte de focus
    const target = e.target as HTMLTextAreaElement;
    selectionStartRef.current = target.selectionStart || 0;
    selectionEndRef.current = target.selectionEnd || 0;
  }, []);

  const handleRetourDefaut = React.useCallback(() => {
    setTexte(texteDefaut);
    setModifie(texteDefaut !== texteInitial.current);
  }, [texteDefaut]);

  const handleInsererElement = React.useCallback(
    (valeur: string) => {
      // Sauvegarder la position de scroll actuelle
      const textareaElement = textareaRef.current;
      const scrollTop = textareaElement?.scrollTop || 0;

      // Utiliser la sélection sauvegardée (début et fin)
      const start = selectionStartRef.current;
      const end = selectionEndRef.current;
      const texteAvant = texte.substring(0, start);
      const texteApres = texte.substring(end);
      const nouveauTexte = texteAvant + valeur + texteApres;

      // Calculer la nouvelle position après l'insertion
      const newPosition = start + valeur.length;

      // Mettre à jour immédiatement les refs pour les prochaines insertions
      selectionStartRef.current = newPosition;
      selectionEndRef.current = newPosition;

      setTexte(nouveauTexte);
      setModifie(nouveauTexte !== texteInitial.current);

      // Remettre le focus, positionner le curseur et restaurer le scroll
      setTimeout(() => {
        if (textareaElement) {
          textareaElement.focus();
          textareaElement.setSelectionRange(newPosition, newPosition);
          textareaElement.scrollTop = scrollTop;
        }
      }, 0);
    },
    [texte],
  );

  const handleEnregistrer = React.useCallback(() => {
    if (onSave) {
      onSave(texte);
    }
  }, [texte, onSave]);

  return (
    <Modal open size="large" onClose={onClose} closeIcon={!loading}>
      <Modal.Header>Personnalisation du document de relance</Modal.Header>
      <Modal.Content scrolling={false}>
        <Grid as={Form}>
          <Grid.Column width={11}>
            <EditeurContenu
              texte={texte}
              loading={loading}
              textareaRef={textareaRef}
              onChange={handleChange}
              onBlur={handleBlur}
              onRetourDefaut={handleRetourDefaut}
            />
          </Grid.Column>
          <Grid.Column width={5}>
            <ElementsDisponibles elements={elements} onInsererElement={handleInsererElement} />
          </Grid.Column>
        </Grid>
      </Modal.Content>
      <Modal.Actions>
        <Button
          data-testid="button-annuler"
          onClick={onClose}
          basic
          size="tiny"
          icon="close"
          content="Annuler"
          disabled={loading}
        />
        <Button
          data-testid="button-enregistrer"
          positive
          onClick={handleEnregistrer}
          content="Enregistrer"
          icon="save"
          disabled={!modifie || loading}
          loading={loading}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default ModalTexteRelance;
